import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Box,
  Center,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Select,
  SimpleGrid,
  Spacer,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useAuth } from "../../services/auth/AuthProvider";
import { getInvoices, invoiceMs, type RawInvoice } from "../../services/api/invoices";
import { getBook, getBooksForPublisher } from "../../services/api/books";
import { getPublishers } from "../../services/api/publishers";
import { getPublishingHouse } from "../../services/api/publishingHouse";
import { DateRangePicker } from "./DateRangePicker";
import { type Period, type Range, rangeFor } from "./period";
import { BookFilter } from "./BookFilter";
import Seo from "../../../../ui/components/Seo/Seo";

const MONTHS = ["Січ", "Лют", "Бер", "Кві", "Тра", "Чер", "Лип", "Сер", "Вер", "Жов", "Лис", "Гру"];

interface LegendItem {
  id: string;
  name: string;
  value: number;
}
interface Stats {
  chart: { x: string; count: number }[];
  topByCount: LegendItem[];
  topByRevenue: LegendItem[];
  totalSales: number;
  totalRevenue: number;
}

function computeStats(
  invoices: RawInvoice[],
  range: Range,
  nameById: Map<string, string>,
  pct: number,
): Stats {
  const start = new Date(range.startIso);
  const end = new Date(range.endIso);
  const buckets = new Map<string, number>();
  if (range.granularity === "day") {
    const cur = new Date(start);
    while (cur <= end) {
      buckets.set(String(cur.getUTCDate()), 0);
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
  } else {
    for (let i = 0; i < 12; i++) buckets.set(MONTHS[i], 0);
  }

  const byBook = new Map<string, { id: string; name: string; count: number; revenue: number }>();
  let totalRevenue = 0;
  for (const inv of invoices) {
    const d = new Date(invoiceMs(inv.createDate));
    const key = range.granularity === "day" ? String(d.getUTCDate()) : MONTHS[d.getUTCMonth()];
    if (buckets.has(key)) buckets.set(key, (buckets.get(key) ?? 0) + 1);

    const amount = Number(inv.amount) || 0;
    const revenue = amount * (pct ?? 1);
    totalRevenue += revenue;

    const e = byBook.get(inv.bookId) ?? { id: inv.bookId, name: nameById.get(inv.bookId) ?? "", count: 0, revenue: 0 };
    e.count += 1;
    e.revenue += revenue;
    byBook.set(inv.bookId, e);
  }

  const chart = [...buckets.entries()].map(([x, count]) => ({ x, count }));
  const books = [...byBook.values()];
  const topByCount = books
    .filter((b) => b.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
    .map((b) => ({ id: b.id, name: b.name, value: b.count }));
  const topByRevenue = books
    .filter((b) => b.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5)
    .map((b) => ({ id: b.id, name: b.name, value: Number(b.revenue.toFixed(2)) }));

  return {
    chart,
    topByCount,
    topByRevenue,
    totalSales: invoices.length,
    totalRevenue: Number(totalRevenue.toFixed(2)),
  };
}

export function StatisticsPage() {
  const { publishingHouse, isSuperadmin } = useAuth();
  const ownName = publishingHouse?.name ?? "";
  const ownPct = publishingHouse?.revenuePercent ?? 1;

  const [period, setPeriod] = useState<Period>("currentMonth");
  const today = new Date().toISOString().slice(0, 10);
  const [customStart, setCustomStart] = useState(`${today.slice(0, 7)}-01`);
  const [customEnd, setCustomEnd] = useState(today);
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [selectedPublisherId, setSelectedPublisherId] = useState<string | null>(null);

  const publishersQuery = useQuery(["publishers"], getPublishers, { enabled: isSuperadmin });
  const publishers = publishersQuery.data ?? [];

  // Effective scope: own house for a regular publisher; for superadmin the
  // chosen house ("" = all publishers).
  const selectedPublisherName = selectedPublisherId
    ? publishers.find((p) => p.id === selectedPublisherId)?.name ?? ""
    : "";
  const effectiveName = isSuperadmin ? selectedPublisherName : ownName;

  // Revenue %: own / the selected house's / 1 (gross) for "all publishers".
  const selectedHouseQuery = useQuery(
    ["ph", selectedPublisherId],
    () => getPublishingHouse(selectedPublisherId as string),
    { enabled: isSuperadmin && !!selectedPublisherId },
  );
  const pct = isSuperadmin
    ? selectedPublisherId
      ? selectedHouseQuery.data?.revenuePercent ?? 1
      : 1
    : ownPct;

  const range = useMemo(() => rangeFor(period, customStart, customEnd), [period, customStart, customEnd]);

  const invoicesQuery = useQuery(
    ["stats-invoices", effectiveName || "ALL", range.startIso, range.endIso],
    () => getInvoices({ startDate: range.startIso, endDate: range.endIso, publishers: effectiveName || undefined }),
    { enabled: isSuperadmin || !!effectiveName, keepPreviousData: true },
  );
  const booksQuery = useQuery(["books", effectiveName], () => getBooksForPublisher(effectiveName), {
    enabled: !!effectiveName,
  });

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    (booksQuery.data ?? []).forEach((b) => m.set(b.id, b.name));
    return m;
  }, [booksQuery.data]);

  const filteredInvoices = useMemo(() => {
    const inv = invoicesQuery.data ?? [];
    return selectedBookId ? inv.filter((i) => i.bookId === selectedBookId) : inv;
  }, [invoicesQuery.data, selectedBookId]);

  const stats = useMemo(
    () => computeStats(filteredInvoices, range, nameById, pct),
    [filteredInvoices, range, nameById, pct],
  );

  // Resolve names for the top books (≤10) — needed for "all publishers", where
  // the per-house book list isn't loaded. Superadmin may read any book.
  const topIds = useMemo(
    () => [...new Set([...stats.topByCount, ...stats.topByRevenue].map((i) => i.id))],
    [stats],
  );
  const topNamesQuery = useQuery(
    ["top-names", topIds.join(",")],
    async () => {
      const entries = await Promise.all(
        topIds.map(
          async (bid) => [bid, (await getBook(bid).catch(() => null))?.name ?? ""] as const,
        ),
      );
      return new Map(entries);
    },
    { enabled: topIds.length > 0 },
  );
  const resolveName = (bid: string, fallback: string) =>
    nameById.get(bid) || topNamesQuery.data?.get(bid) || fallback || "";
  const topByCount = stats.topByCount.map((it) => ({ ...it, name: resolveName(it.id, it.name) }));
  const topByRevenue = stats.topByRevenue.map((it) => ({ ...it, name: resolveName(it.id, it.name) }));

  const loading = invoicesQuery.isLoading || invoicesQuery.isFetching;

  return (
    <Flex direction="column" minH="100%">
      <Seo
        title="Статистика — Кабінет видавця"
        description="Статистика продажів книг видавництва"
        noindex
      />
      <Box position="sticky" top={0} zIndex={20} bg="white" borderBottom="1px solid" borderColor="blackAlpha.100" px={6} py={4}>
        <Heading size="lg" fontWeight="medium" letterSpacing="wide" textTransform="uppercase">
          {"Статистика"}
        </Heading>
      </Box>

      <VStack align="stretch" spacing={4} p={6}>
        <Card>
          <Flex align="flex-end" gap={3} wrap="wrap">
            <FormControl w="fit-content">
              <FormLabel fontSize="xs">{"Період"}</FormLabel>
              <Select value={period} onChange={(e) => setPeriod(e.target.value as Period)} size="sm">
                <option value="currentMonth">{"Поточний місяць"}</option>
                <option value="previousMonth">{"Попередній місяць"}</option>
                <option value="currentYear">{"Поточний рік"}</option>
                <option value="previousYear">{"Попередній рік"}</option>
                <option value="custom">{"Інша дата"}</option>
              </Select>
            </FormControl>

            {period === "custom" && (
              <FormControl w="fit-content">
                <FormLabel fontSize="xs">{"Інша дата"}</FormLabel>
                <DateRangePicker
                  start={customStart}
                  end={customEnd}
                  placeholder={"Обрати діапазон"}
                  onChange={(s, e) => {
                    setCustomStart(s);
                    setCustomEnd(e);
                  }}
                />
              </FormControl>
            )}

            {isSuperadmin && (
              <FormControl w="fit-content">
                <FormLabel fontSize="xs">{"Видавництва"}</FormLabel>
                <BookFilter
                  books={publishers}
                  value={selectedPublisherId}
                  onChange={(pid) => {
                    setSelectedPublisherId(pid);
                    setSelectedBookId(null);
                  }}
                  allLabel={"Всі видавництва"}
                  searchPlaceholder={"Пошук"}
                />
              </FormControl>
            )}

            <FormControl w="fit-content">
              <FormLabel fontSize="xs">{"Книги"}</FormLabel>
              <BookFilter
                books={(booksQuery.data ?? []).map((b) => ({ id: b.id, name: b.name }))}
                value={selectedBookId}
                onChange={setSelectedBookId}
                allLabel={"Всі книги"}
                searchPlaceholder={"Пошук"}
              />
            </FormControl>

            <Spacer />
            <HStack spacing={8} align="flex-end">
              <Stat label={"Продажі"} value={String(stats.totalSales)} />
              <Stat label={"Дохід"} value={`₴${stats.totalRevenue.toFixed(2)}`} />
            </HStack>
          </Flex>
        </Card>

        <Card>
          <Box h="320px" w="full">
            {loading ? (
              <Center h="full">
                <Spinner color="brand.orange" />
              </Center>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.chart} margin={{ top: 16, right: 24, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="x" tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                  <Tooltip />
                  <Bar dataKey="count" name={"Продажі"} fill="#FF7C49" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Card>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Legend title={"Топ книг за кількістю"} items={topByCount} suffix="" />
          <Legend title={"Топ книг за прибутком"} items={topByRevenue} suffix=" ₴" />
        </SimpleGrid>
      </VStack>
    </Flex>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box bg="white" borderRadius="xl" border="1px solid" borderColor="blackAlpha.100" boxShadow="sm" p={4}>
      {children}
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Box textAlign="right">
      <Text fontSize="xl" fontWeight="semibold">{value}</Text>
      <Text fontSize="xs" color="gray.500">{label}</Text>
    </Box>
  );
}

function Legend({ title, items, suffix }: { title: string; items: LegendItem[]; suffix: string }) {
  return (
    <Card>
      <Text mb={3} fontSize="sm" fontWeight="medium">{title}</Text>
      {items.length === 0 ? (
        <Text fontSize="sm" color="gray.500">{"Немає даних"}</Text>
      ) : (
        <VStack align="stretch" spacing={2}>
          {items.map((it, i) => (
            <Flex key={i} align="center" justify="space-between" gap={3} fontSize="sm">
              <HStack minW={0} spacing={2}>
                <Box w={4} h="2px" bg="brand.orange" flexShrink={0} />
                <Text noOfLines={1}>{it.name || "—"}</Text>
              </HStack>
              <Text color="gray.500" flexShrink={0}>
                {it.value}
                {suffix}
              </Text>
            </Flex>
          ))}
        </VStack>
      )}
    </Card>
  );
}
